import type { PropsWithChildren } from "react";
import { useState, useEffect } from "react";
import { PopperPlacements } from "@certa/catalyst/components/types";
import { PopoverOld } from "@certa/catalyst/components/Popover";
import { css } from "emotion";
import type { ObjectFilterGroup } from "./types";
import {
  createEmptyGroup,
  createDefaultFilter,
  isFilterCondition,
  isConditionComplete
} from "./utils";
import { ObjectFilterButton } from "./ObjectFilterButton";
import { countRules } from "./useObjectFilterRules";

type ObjectFilterPopoverProps = {
  value?: ObjectFilterGroup;
  onChange?: (value: ObjectFilterGroup) => void;
  maxHeight?: string;
  maxWidth?: string;
  disabled?: boolean;
  selectedObjectType?: string;
};

export const ObjectFilterPopover = (
  props: PropsWithChildren<ObjectFilterPopoverProps>
) => {
  const {
    children,
    maxHeight = "400px",
    maxWidth = "70vw",
    disabled: isDisabled = false
  } = props;

  const { onVisibleChange, noOfRules } = useObjectFilterPopover(props);

  return (
    <PopoverOld
      aria-label="Advanced object filters"
      content={
        <div className={popoverContentStyle} style={{ maxHeight, maxWidth }}>
          {children}
        </div>
      }
      placement={PopperPlacements.TOP_END}
      asChild
      visible={true}
      onVisibleChange={onVisibleChange}
    >
      <span style={{ width: "150px" }}>
        <ObjectFilterButton noOfRules={noOfRules} disabled={isDisabled} />
      </span>
    </PopoverOld>
  );
};

const DEFAULT_COUNT = 0;
const COUNT_ONE = 1;

const useObjectFilterPopover = (props: ObjectFilterPopoverProps) => {
  const {
    value,
    onChange,
    disabled: isDisabled = false,
    selectedObjectType
  } = props;
  const [noOfRules, setNoOfRules] = useState(countRules(value));

  const updateConditionBasedOnCount = (visible: boolean) => {
    let newCount = countRules(value);

    if (visible) {
      // If there are no rules, add one condition
      if (newCount === DEFAULT_COUNT && selectedObjectType) {
        const newFilter = createDefaultFilter(selectedObjectType);
        onChange?.(newFilter);
        newCount = COUNT_ONE;
      }
    } else {
      // If there is only one rule and it is empty, reset the filter
      if (
        newCount === COUNT_ONE &&
        value?.conditions?.length === COUNT_ONE &&
        value.conditions[0] &&
        isFilterCondition(value.conditions[0]) &&
        !isConditionComplete(value.conditions[0])
      ) {
        const cleanFilter = createEmptyGroup();
        onChange?.(cleanFilter);
        newCount = DEFAULT_COUNT;
      }
      setNoOfRules(newCount);
    }
  };

  const onVisibleChange = (visible: boolean) => {
    if (isDisabled) return;
    updateConditionBasedOnCount(visible);
  };

  // Update rule count when filter changes
  useEffect(() => {
    setNoOfRules(countRules(value));
  }, [value]);

  return {
    onVisibleChange,
    noOfRules
  };
};

const popoverContentStyle = css`
  padding: var(--s4) var(--s6);
  overflow-y: auto;
  overflow-x: auto;
`;
